/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useElevenLabsContext } from '../../contexts/ElevenLabsContext';
import './TextToSpeech.scss';

export interface TextToSpeechProps {
  text?: string;
  onTextChange?: (text: string) => void;
  autoPlay?: boolean;
  showVoiceSelector?: boolean;
  showSettings?: boolean;
  className?: string;
}

export const TextToSpeech: React.FC<TextToSpeechProps> = ({
  text = '',
  onTextChange,
  autoPlay = false,
  showVoiceSelector = true,
  showSettings = true,
  className = '',
}) => {
  const {
    voices,
    currentVoice,
    isSpeaking,
    speakText,
    stopSpeaking,
    setVoice,
    isLoading,
    error,
    clearError,
  } = useElevenLabsContext();

  const [localText, setLocalText] = useState(text);
  const [stability, setStability] = useState(0.5);
  const [similarityBoost, setSimilarityBoost] = useState(0.5);
  const [style, setStyle] = useState(0.0);
  const [speakerBoost, setSpeakerBoost] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Update local text when prop changes
  useEffect(() => {
    if (text !== localText) {
      setLocalText(text);
    }
  }, [text, localText]);

  // Handle text change
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setLocalText(newText);
    onTextChange?.(newText);
  };

  // Handle speak
  const handleSpeak = async () => {
    if (!localText.trim()) return;
    
    try {
      await speakText({
        text: localText,
        voice_id: currentVoice?.voice_id,
        stability,
        similarity_boost: similarityBoost,
        style,
        speaker_boost: speakerBoost,
      });
    } catch (err) {
      console.error('Speech error:', err);
    }
  };

  // Handle stop
  const handleStop = () => {
    stopSpeaking();
  };

  // Handle voice change
  const handleVoiceChange = (voiceId: string) => {
    const voice = voices.find(v => v.voice_id === voiceId);
    if (voice) {
      setVoice(voice);
    }
  };

  // Auto-play functionality
  useEffect(() => {
    if (autoPlay && localText.trim() && !isSpeaking) {
      handleSpeak();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, localText, isSpeaking]);

  return (
    <div className={`text-to-speech ${className}`}>
      {/* Error Display */}
      {error && (
        <div className="text-to-speech__error">
          <span>{error}</span>
          <button onClick={clearError} className="text-to-speech__error-close">
            ×
          </button>
        </div>
      )}

      {/* Text Input */}
      <div className="text-to-speech__input">
        <textarea
          ref={textAreaRef}
          value={localText}
          onChange={handleTextChange}
          placeholder="Enter text to convert to speech..."
          className="text-to-speech__textarea"
          rows={4}
          disabled={isLoading}
        />
        <div className="text-to-speech__char-count">
          {localText.length} characters
        </div>
      </div>

      {/* Voice Selector */}
      {showVoiceSelector && (
        <div className="text-to-speech__voice-selector">
          <label htmlFor="voice-select">Voice:</label>
          <select
            id="voice-select"
            value={currentVoice?.voice_id || ''}
            onChange={(e) => handleVoiceChange(e.target.value)}
            disabled={isLoading || voices.length === 0}
            className="text-to-speech__select"
          >
            <option value="">Select a voice...</option>
            {voices.map((voice) => (
              <option key={voice.voice_id} value={voice.voice_id}>
                {voice.name} - {voice.gender} ({voice.accent})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Advanced Settings */}
      {showSettings && (
        <div className="text-to-speech__settings">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-to-speech__settings-toggle"
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
          </button>

          {showAdvanced && (
            <div className="text-to-speech__advanced">
              <div className="text-to-speech__setting">
                <label htmlFor="stability">
                  Stability: {stability.toFixed(2)}
                </label>
                <input
                  id="stability"
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={stability}
                  onChange={(e) => setStability(parseFloat(e.target.value))}
                  disabled={isLoading}
                />
              </div>

              <div className="text-to-speech__setting">
                <label htmlFor="similarity">
                  Similarity Boost: {similarityBoost.toFixed(2)}
                </label>
                <input
                  id="similarity"
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={similarityBoost}
                  onChange={(e) => setSimilarityBoost(parseFloat(e.target.value))}
                  disabled={isLoading}
                />
              </div>

              <div className="text-to-speech__setting">
                <label htmlFor="style">
                  Style: {style.toFixed(2)}
                </label>
                <input
                  id="style"
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={style}
                  onChange={(e) => setStyle(parseFloat(e.target.value))}
                  disabled={isLoading}
                />
              </div>

              <div className="text-to-speech__setting">
                <label className="text-to-speech__checkbox-label">
                  <input
                    type="checkbox"
                    checked={speakerBoost}
                    onChange={(e) => setSpeakerBoost(e.target.checked)}
                    disabled={isLoading}
                  />
                  Speaker Boost
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="text-to-speech__controls">
        <button
          onClick={isSpeaking ? handleStop : handleSpeak}
          disabled={!localText.trim() || isLoading}
          className={`text-to-speech__button ${
            isSpeaking ? 'text-to-speech__button--stop' : 'text-to-speech__button--speak'
          }`}
        >
          {isLoading ? 'Loading...' : isSpeaking ? 'Stop' : 'Speak'}
        </button>

        {currentVoice && (
          <div className="text-to-speech__current-voice">
            Current: {currentVoice.name}
          </div>
        )}
      </div>

      {/* Voice Info */}
      {currentVoice && showVoiceSelector && (
        <div className="text-to-speech__voice-info">
          <h4>Voice Details</h4>
          <p><strong>Name:</strong> {currentVoice.name}</p>
          <p><strong>Gender:</strong> {currentVoice.gender}</p>
          <p><strong>Age:</strong> {currentVoice.age}</p>
          <p><strong>Accent:</strong> {currentVoice.accent}</p>
          <p><strong>Use Case:</strong> {currentVoice.use_case}</p>
          {currentVoice.description && (
            <p><strong>Description:</strong> {currentVoice.description}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default TextToSpeech;
